/**
 * Haptics for the things that happen *to* the player.
 *
 * A leak, a life count crossing into the red, a boss arriving, the run ending.
 * Never a shot, a kill or a placement — those are the player's own output and
 * they already have sound, light and a number floating off them.
 *
 * ## What each platform actually gets
 *
 * The vocabulary here is modelled on the WICG Web Haptics explainer
 * (https://github.com/WICG/web-haptics): four named effects — `hint`, `edge`,
 * `tick`, `align` — played through `navigator.playHaptics(effect, intensity)`.
 * That proposal is at the ideation stage and **no browser ships it**, so it is
 * only ever the first thing tried, and today it is never the thing that runs.
 *
 * What runs instead, in the order the adapter tries them:
 *
 * | Platform | Path | Result |
 * |---|---|---|
 * | Any browser, gamepad connected | `Gamepad.vibrationActuator` (`dual-rumble`), else the legacy `hapticActuators[0].pulse` | Real amplitude, in the pad |
 * | Chromium on Android (and Android WebViews) | `navigator.vibrate(pattern)` | Duration only, no amplitude |
 * | iOS Safari, desktop Chrome/Firefox/Safari, Node | — | Silent no-op |
 *
 * iOS exposes no vibration API to the web at all, and desktop Chromium exposes
 * `navigator.vibrate` while doing nothing with it. So a desktop pad is checked
 * first: a connected actuator is a definite signal, `vibrate`'s presence is not.
 *
 * The pad path plays one `dual-rumble` for the pattern's total buzz time rather
 * than reproducing its rhythm. Queueing segments would need a timer chain, and
 * `playEffect` cancels whatever is already running on the same actuator; the
 * weight survives, the rhythm does not.
 *
 * ## Saturation
 *
 * A leak fires often in a bad round and continuous buzz is the fastest way to
 * make a player switch a feature off. The rule is the one `game/World.ts` uses
 * for screen shake, turned around: accumulate, saturate, decay — but where
 * shake grows toward a ceiling, a haptic *shrinks* toward silence. Each played
 * pattern adds its own buzz milliseconds to a decaying accumulator, and the
 * gain applied to the next one is `exp(-trauma / knee)`. Eight rapid leaks walk
 * the gain under the floor and the tenth is skipped entirely; a second of quiet
 * brings it back. Nothing here imports from `game/*` or `render/*`.
 */

/** The events worth a vibration. Everything else on the board is player output. */
export type HapticEvent =
  | 'leak'
  | 'livesCritical'
  | 'bossArrival'
  | 'defeat'
  | 'victory';

/** The four effects the WICG explainer names. Tried first, shipped nowhere yet. */
export type WicgEffect = 'hint' | 'edge' | 'tick' | 'align';

export interface HapticPattern {
  /** What this would be if `navigator.playHaptics` existed. */
  effect: WicgEffect;
  /**
   * Milliseconds in `navigator.vibrate` form: buzz, pause, buzz, pause… The
   * first entry is always a buzz and the length is always odd, so a pattern
   * never ends on a pause nobody can feel.
   */
  pattern: readonly number[];
  /** Low-frequency pad magnitude, 0..1. The weight of the thing. */
  strong: number;
  /** High-frequency pad magnitude, 0..1. The edge of it. */
  weak: number;
  /** Whether repeats of this event fade each other out. Only the frequent one. */
  saturates: boolean;
  /** Whether `intensity` scales the buzz durations, or the pattern is fixed. */
  scaled: boolean;
}

/**
 * The bank. Buzz durations are the *maximum* — `scaled` patterns reach them
 * only at intensity 1, and everything is multiplied by the saturation gain.
 */
export const HAPTIC_PATTERNS: Readonly<Record<HapticEvent, HapticPattern>> = {
  /**
   * A leak. One tick, sharp, its length taken from how many lives went with it:
   * a swarmling is a flick, the round-100 colossus is a thump.
   */
  leak: {
    effect: 'edge',
    pattern: [45],
    strong: 0.55,
    weak: 0.4,
    saturates: true,
    scaled: true,
  },
  /**
   * The life count crossing into the red the HUD pulses at. Two beats, because
   * one more tick after a leak's tick would read as part of the leak.
   */
  livesCritical: {
    effect: 'edge',
    pattern: [35, 70, 35],
    strong: 0.7,
    weak: 0.5,
    saturates: false,
    scaled: false,
  },
  /** A boss is coming. Long and low, felt rather than noticed. */
  bossArrival: {
    effect: 'edge',
    pattern: [420],
    strong: 0.85,
    weak: 0.15,
    saturates: false,
    scaled: false,
  },
  /**
   * The run is over. Heavy and fast at the front, then longer gaps and shorter
   * beats, so it dies away on the same curve the board does.
   */
  defeat: {
    effect: 'edge',
    pattern: [110, 45, 85, 55, 65, 75, 45, 95, 30, 130, 18],
    strong: 1,
    weak: 0.6,
    saturates: false,
    scaled: false,
  },
  /** Three beats, rising, done in a quarter second. */
  victory: {
    effect: 'align',
    pattern: [30, 50, 30, 50, 90],
    strong: 0.5,
    weak: 0.7,
    saturates: false,
    scaled: false,
  },
};

/**
 * Tuned against the round the exit is open: a leak every 50ms for two seconds.
 * Four ticks land, each shorter than the last, then it refuses until the
 * accumulator has decayed — steady state is one ~20ms tick every fifth of a
 * second, a tenth of the wall time. A drone is what makes a player switch the
 * whole feature off, and a tenth of a duty cycle is nowhere near one.
 */
export const SATURATION = {
  /** Buzz-ms of recent history at which the gain has fallen to ~37%. */
  knee: 140,
  /** Decay time constant in ms. Recovery is deliberately slower than the burst. */
  tau: 2000,
  /** Ceiling on the accumulator, so one catastrophic frame cannot mute minutes. */
  max: 600,
  /** Below this gain a tick is a hardware wake-up nobody feels. Skip it. */
  floor: 0.45,
  /** Two events inside one frame never stack into one long buzz. */
  minGapMs: 40,
} as const;

export const REDUCED_MOTION = {
  /** Applied on top of the saturation gain, never instead of it. */
  gain: 0.5,
  /** Hard ceiling on total buzz per pattern once the gain is applied. */
  maxTotalMs: 180,
} as const;

/** Smallest buzz worth sending. Under this the hardware spin-up is the effect. */
const MIN_SEGMENT_MS = 8;

/** The leak size that earns the full pattern. The round-100 boss takes 100. */
const LEAK_FULL = 25;

/**
 * Lives lost by one leak, mapped to 0..1.
 *
 * Logarithmic because the range is 1 to 100 and the interesting half of it is
 * at the bottom: a 1-life swarmling and a 4-life brute have to feel different,
 * a 45-life boss and a 100-life boss do not.
 */
export function leakIntensity(livesLost: number): number {
  if (!(livesLost > 0)) return 0;
  const t = Math.log(1 + livesLost) / Math.log(1 + LEAK_FULL);
  return t > 1 ? 1 : t;
}

/** What the adapter would use right now. `'none'` means every call is a no-op. */
export type HapticBackend = 'wicg' | 'gamepad' | 'vibrate' | 'none';

// ─────────────────────────────────────────────────────────────────── state ──

let enabled = true;
let forcedReducedMotion = false;
let trauma = 0;
let lastFireAt = -1e9;
/**
 * When the accumulator was last aged.
 *
 * Separate from `lastFireAt` on purpose. Measuring the decay from the last
 * *accepted* pattern applies the same stretch of quiet once per refused call as
 * well, and a stream of refusals then decays the accumulator many times faster
 * than the wall clock justifies — which is exactly the drone the rule exists to
 * prevent. Every call ages it, once.
 */
let lastDecayAt = -1e9;
let padIndex = -1;
let padWatched = false;

/**
 * Wall clock, swappable so the tests can drive the decay without sleeping.
 * Nothing in the game should call the setter.
 */
let clock: () => number = () =>
  typeof performance !== 'undefined' ? performance.now() : Date.now();

export function setHapticClock(fn: () => number): void {
  clock = fn;
}

/** The switch behind the Comfort settings toggle. */
export function setHapticsEnabled(on: boolean): void {
  enabled = on;
}

export function hapticsEnabled(): boolean {
  return enabled;
}

/**
 * The settings sheet's Reduce-motion toggle, which is only ever additive: the
 * OS preference is read live and this cannot turn it off.
 */
export function setHapticsReducedMotion(forced: boolean): void {
  forcedReducedMotion = forced;
}

/** Clears the saturation accumulator. Called between runs, and by the tests. */
export function resetHaptics(): void {
  trauma = 0;
  lastFireAt = -1e9;
  lastDecayAt = -1e9;
}

// ───────────────────────────────────────────────────────────────── adapter ──

interface WicgNavigator {
  playHaptics?(effect: string, intensity?: number): unknown;
}

interface PadActuator {
  playEffect?(type: string, params: Record<string, number>): Promise<unknown>;
  pulse?(value: number, duration: number): Promise<unknown>;
}

interface PadLike {
  vibrationActuator?: PadActuator | null;
  hapticActuators?: readonly PadActuator[] | null;
}

/**
 * Caches which pad slot has an actuator.
 *
 * `getGamepads()` allocates a fresh array on every call, and the leak path can
 * ask several times a second, so the index is remembered and only the connect
 * and disconnect events invalidate it.
 */
function currentPad(): PadLike | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as Navigator & {
    getGamepads?(): readonly (PadLike | null)[];
  };
  if (typeof nav.getGamepads !== 'function') return null;

  if (!padWatched && typeof window !== 'undefined') {
    padWatched = true;
    const invalidate = (): void => {
      padIndex = -1;
    };
    window.addEventListener('gamepadconnected', invalidate);
    window.addEventListener('gamepaddisconnected', invalidate);
  }

  let pads: readonly (PadLike | null)[];
  try {
    pads = nav.getGamepads();
  } catch {
    return null;
  }

  if (padIndex >= 0) {
    const cached = pads[padIndex] ?? null;
    if (cached !== null && actuatorOf(cached) !== null) return cached;
    padIndex = -1;
  }
  for (let i = 0; i < pads.length; i++) {
    const pad = pads[i];
    if (pad !== null && pad !== undefined && actuatorOf(pad) !== null) {
      padIndex = i;
      return pad;
    }
  }
  return null;
}

function actuatorOf(pad: PadLike): PadActuator | null {
  const primary = pad.vibrationActuator ?? null;
  if (primary !== null && typeof primary.playEffect === 'function') return primary;
  const legacy = pad.hapticActuators?.[0] ?? null;
  if (legacy !== null && typeof legacy.pulse === 'function') return legacy;
  return null;
}

/**
 * The backend a call would take right now, without playing anything.
 * Useful in the settings sheet and in the tests; the adapter re-resolves per
 * call because a pad can arrive at any moment.
 */
export function hapticBackend(): HapticBackend {
  if (typeof navigator === 'undefined') return 'none';
  if (typeof (navigator as WicgNavigator).playHaptics === 'function') return 'wicg';
  if (currentPad() !== null) return 'gamepad';
  if (typeof navigator.vibrate === 'function') return 'vibrate';
  return 'none';
}

function prefersReducedMotion(): boolean {
  if (forcedReducedMotion) return true;
  if (typeof matchMedia !== 'function') return false;
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// ──────────────────────────────────────────────────────────────────── fire ──

const scratch: number[] = [];

/**
 * Plays the pattern for an event, if the platform has anything to play it with.
 *
 * `intensity` is the WICG parameter: 0..1, default 1, and only the patterns
 * marked `scaled` read it. Returns whether anything was actually sent, which is
 * `false` on every desktop browser and on iOS.
 *
 * Every path into platform code is wrapped: `vibrate` throws in some embedded
 * and cross-origin-iframe contexts rather than returning `false`, and a pad
 * actuator rejects its promise when the pad disconnects mid-effect.
 */
export function haptic(event: HapticEvent, intensity = 1): boolean {
  if (!enabled) return false;

  const bank = HAPTIC_PATTERNS[event];
  if (bank === undefined) return false;

  const now = clock();
  if (now - lastFireAt < SATURATION.minGapMs) return false;

  // Age first, so the gain reflects the quiet since the last time anyone asked.
  const elapsed = now - lastDecayAt;
  lastDecayAt = now;
  if (elapsed > 0 && trauma > 0) {
    trauma *= Math.exp(-elapsed / SATURATION.tau);
    if (trauma < 0.5) trauma = 0;
  }

  let gain = bank.saturates ? Math.exp(-trauma / SATURATION.knee) : 1;
  if (bank.saturates && gain < SATURATION.floor) return false;

  if (bank.scaled) {
    const i = intensity < 0 ? 0 : intensity > 1 ? 1 : intensity;
    // Never below a third: a 1-life leak still has to be felt as a leak.
    gain *= 0.35 + 0.65 * i;
  }

  /*
   * Reduced motion caps rather than silences.
   *
   * The preference exists for vestibular disruption from moving pixels, and a
   * vibration moves none. What it is still the best available signal for is
   * "keep the sensory load down", so the gain is halved and the long rumbles
   * lose their tails. Turning haptics off here instead would make the Comfort
   * toggle lie — it would read "Haptics: on" while nothing ever fired, and the
   * player would have no way to tell that from broken hardware.
   */
  const quiet = prefersReducedMotion();
  if (quiet) gain *= REDUCED_MOTION.gain;

  const budget = quiet ? REDUCED_MOTION.maxTotalMs : Infinity;
  scratch.length = 0;
  let buzz = 0;
  for (let i = 0; i < bank.pattern.length; i++) {
    const raw = bank.pattern[i];
    if ((i & 1) === 1) {
      scratch.push(Math.round(raw));
      continue;
    }
    let ms = Math.round(raw * gain);
    if (ms > budget - buzz) ms = Math.round(budget - buzz);
    if (ms < MIN_SEGMENT_MS) break;
    scratch.push(ms);
    buzz += ms;
  }
  // A trailing pause is a pause nobody feels; drop it.
  if ((scratch.length & 1) === 0) scratch.pop();
  if (scratch.length === 0 || buzz <= 0) return false;

  lastFireAt = now;
  if (bank.saturates) {
    trauma += buzz;
    if (trauma > SATURATION.max) trauma = SATURATION.max;
  }

  return play(bank, scratch, buzz, gain);
}

function play(
  bank: HapticPattern,
  pattern: number[],
  buzz: number,
  gain: number,
): boolean {
  if (typeof navigator === 'undefined') return false;

  // 1. The API the WICG explainer proposes. Nothing implements it yet.
  const wicg = (navigator as WicgNavigator).playHaptics;
  if (typeof wicg === 'function') {
    try {
      wicg.call(navigator, bank.effect, gain > 1 ? 1 : gain);
      return true;
    } catch {
      // Fall through: a present-but-throwing implementation is not a backend.
    }
  }

  // 2. A connected pad, where amplitude is real.
  const pad = currentPad();
  if (pad !== null) {
    const act = actuatorOf(pad);
    if (act !== null) {
      // The Gamepad spec caps a single effect at five seconds.
      const duration = Math.min(5000, buzz);
      try {
        if (typeof act.playEffect === 'function') {
          void act.playEffect('dual-rumble', {
            startDelay: 0,
            duration,
            strongMagnitude: clamp01(bank.strong * gain),
            weakMagnitude: clamp01(bank.weak * gain),
          }).catch(() => {});
          return true;
        }
        if (typeof act.pulse === 'function') {
          void act.pulse(clamp01(bank.strong * gain), duration).catch(() => {});
          return true;
        }
      } catch {
        // A pad that disconnected between the lookup and the call.
      }
    }
  }

  // 3. Chromium on Android. Duration only.
  if (typeof navigator.vibrate === 'function') {
    try {
      // The scratch array goes straight in: `vibrate` reads it synchronously,
      // and rule 3 in the architecture contract says pool everything.
      // It returns false rather than throwing when no gesture has happened yet.
      return navigator.vibrate(pattern) !== false;
    } catch {
      return false;
    }
  }

  return false;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
